/**
 * Книга пригод: what there is to do, how far you are, and what you got.
 *
 * A column of the inventory window, beside the recipe book and never at the
 * same time as it. Each quest is a row: its name, what it asks in a sentence,
 * a bar showing how much of the asked-for thing is in the bag, and the reward.
 *
 * A finished one grows a «Забрати» button. Pressing that is what marks the
 * quest done - not finishing it - because the player has to be told they were
 * paid, and a quest that ticks itself the moment the third стебло lands is a
 * quest whose reward appears in the bag from nowhere.
 *
 * Redrawn from the world rather than kept in step with it: `refresh` reads
 * the bag and the claimed set every time. A quest book is a page somebody
 * looks at, not a thing that has to keep up with a frame loop.
 */

import { pick, ui, type Lang } from "../core/text/lang.js";
import {
  QUESTS, questProgress, claimQuest, type Quest, type ClaimRefusal,
} from "../core/quest/quests.js";
import type { Inventory } from "../core/inventory/inventory.js";
import { itemTile, itemLabel } from "../core/block/items.js";
import { ATLAS_TILES } from "../core/block/tiles.js";
import { buildAtlasCanvas } from "../render/atlas.js";

const ICON_PX = 28;

export class QuestBook {
  private readonly inventory: Inventory;
  private readonly claimed: Set<string>;
  private readonly atlasUrl: string;
  private readonly listEl: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly emptyEl: HTMLElement;
  /** Rebuilt per refresh, so a row never outlives the state it was drawn from. */
  private readonly rows = new Map<string, HTMLElement>();

  /** Fired after a reward has been taken, so the bag and belt can redraw. */
  onClaim: ((quest: Quest) => void) | null = null;
  /** Fired when a claim was refused, with the reason, so it can be said out loud. */
  onRefused: ((why: ClaimRefusal) => void) | null = null;

  lang: Lang = "uk";

  constructor(inventory: Inventory, claimed: Set<string>, root: HTMLElement) {
    this.inventory = inventory;
    this.claimed = claimed;
    this.atlasUrl = buildAtlasCanvas().toDataURL();

    this.titleEl = document.createElement("div");
    this.titleEl.className = "book-title";

    this.listEl = document.createElement("div");
    this.listEl.className = "quest-list";

    this.emptyEl = document.createElement("div");
    this.emptyEl.className = "recipe-empty";
    this.emptyEl.hidden = true;

    root.append(this.titleEl, this.listEl, this.emptyEl);
    this.retitle();
    this.refresh();
  }

  retitle(): void {
    this.titleEl.textContent = ui("quests-title", this.lang);
    this.emptyEl.textContent = ui("quests-none", this.lang);
    this.refresh();
  }

  private icon(id: number): HTMLElement {
    const el = document.createElement("div");
    el.className = "recipe-icon";
    el.style.width = `${ICON_PX}px`;
    el.style.height = `${ICON_PX}px`;
    const tile = itemTile(id);
    const sheet = ICON_PX * ATLAS_TILES;
    el.style.backgroundImage = `url(${this.atlasUrl})`;
    el.style.backgroundSize = `${sheet}px ${sheet}px`;
    el.style.backgroundPosition =
      `${-(tile % ATLAS_TILES) * ICON_PX}px ` +
      `${-Math.floor(tile / ATLAS_TILES) * ICON_PX}px`;
    el.title = itemLabel(id);
    return el;
  }

  /**
   * Draw the lot again.
   *
   * Rows are rebuilt rather than patched. There are a handful of them, they
   * are only looked at when the page is open, and a row rebuilt from the
   * world cannot be showing a number the world stopped agreeing with.
   */
  refresh(): void {
    this.listEl.replaceChildren();
    this.rows.clear();
    this.emptyEl.hidden = QUESTS.length > 0;

    for (const quest of QUESTS) {
      const done = this.claimed.has(quest.id);
      const have = questProgress(quest, this.inventory);
      const ready = !done && have >= quest.want.count;

      const row = document.createElement("div");
      row.className = "quest-row";
      row.classList.toggle("done", done);
      row.classList.toggle("ready", ready);

      const head = document.createElement("div");
      head.className = "quest-head";
      const name = document.createElement("span");
      name.className = "quest-name";
      name.textContent = pick(quest.name, this.lang);
      head.append(name);
      // The tick, and only on a claimed one: this is what "пройдено" means.
      if (done) {
        const tick = document.createElement("span");
        tick.className = "quest-tick";
        tick.textContent = `✓ ${ui("quests-done", this.lang)}`;
        head.append(tick);
      }

      const blurb = document.createElement("div");
      blurb.className = "quest-blurb";
      blurb.textContent = pick(quest.blurb, this.lang);

      // How far along, in words and as a bar. The words are what a player
      // reads; the bar is what they see without reading.
      const tally = document.createElement("div");
      tally.className = "quest-tally";
      const bar = document.createElement("div");
      bar.className = "quest-bar";
      const fill = document.createElement("div");
      fill.className = "quest-fill";
      fill.style.width = `${(have / quest.want.count) * 100}%`;
      bar.append(fill);
      const count = document.createElement("span");
      count.className = "quest-count";
      count.textContent = done
        ? `${quest.want.count} / ${quest.want.count}`
        : `${have} / ${quest.want.count}  ${itemLabel(quest.want.item)}`;
      tally.append(bar, count);

      const foot = document.createElement("div");
      foot.className = "quest-foot";
      const rewardLabel = document.createElement("span");
      rewardLabel.className = "quest-reward-label";
      rewardLabel.textContent = ui("quests-reward", this.lang);
      foot.append(rewardLabel, this.icon(quest.reward.id));
      const rewardName = document.createElement("span");
      rewardName.className = "quest-reward-name";
      rewardName.textContent = quest.reward.count > 1
        ? `${itemLabel(quest.reward.id)} ×${quest.reward.count}`
        : itemLabel(quest.reward.id);
      foot.append(rewardName);

      if (ready) {
        const take = document.createElement("button");
        take.type = "button";
        take.className = "quest-claim";
        take.textContent = ui("quests-claim", this.lang);
        take.addEventListener("click", () => this.claim(quest));
        foot.append(take);
      }

      row.append(head, blurb, tally, foot);
      this.listEl.append(row);
      this.rows.set(quest.id, row);
    }
  }

  private claim(quest: Quest): void {
    const why = claimQuest(quest, this.inventory, this.claimed);
    if (why !== null) {
      this.onRefused?.(why);
      // Redraw anyway: whatever it was, the page was showing something that
      // is no longer true.
      this.refresh();
      return;
    }
    this.refresh();
    this.onClaim?.(quest);
  }
}
